import 'katex/dist/katex.min.css';

import { CaptionedBlockComponent } from '@blocksuite/affine-components/caption';
import { createLitPortal } from '@blocksuite/affine-components/portal';
import katex from 'katex';
import { css, html, render } from 'lit';
import { customElement, query } from 'lit/decorators.js';

import type { PiqueLatexBlockModel } from './latex-block';

export const latexBlockStyles = css`
  affine-latex {
    display: block;
  }

  .katex-mathml {
    position: absolute;
    clip: rect(1px, 1px, 1px, 1px);
    padding: 0;
    border: 0;
    height: 1px;
    width: 1px;
    overflow: hidden;
  }

  .latex-block-container {
    display: flex;
    position: relative;
    width: 100%;
    min-height: 60px;
    padding: 10px 24px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    overflow-x: auto;
    user-select: none;
  }

  .latex-block-container:hover {
    background: var(--affine-hover-color);
  }

  .latex-block-error-placeholder {
    color: var(--affine-error-color, #eb4335);
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: normal;
    user-select: none;
  }

  .latex-block-empty-placeholder {
    color: var(--affine-placeholder-color);
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: normal;
    user-select: none;
  }
`;

@customElement('affine-latex')
export class LatexBlockComponent extends CaptionedBlockComponent<PiqueLatexBlockModel> {
  static override styles = latexBlockStyles;

  private _editorAbortController: AbortController | null = null;

  @query('.latex-block-container')
  private accessor _katexContainer!: HTMLDivElement;

  get isBlockSelected() {
    const blockSelection = this.selection.filter('block');
    return blockSelection.some(sel => sel.blockId === this.model.id);
  }

  override connectedCallback() {
    super.connectedCallback();
    this.contentEditable = 'false';
  }

  override firstUpdated(props: Map<string, unknown>) {
    super.firstUpdated(props);

    const { disposables } = this;

    this._editorAbortController?.abort();
    this._editorAbortController = new AbortController();
    disposables.add(() => {
      this._editorAbortController?.abort();
    });

    const katexContainer = this._katexContainer;
    if (!katexContainer) return;

    this._renderLatex();

    disposables.add(
      this.model.propsUpdated.on(() => {
        this._renderLatex();
      })
    );
  }

  private _renderLatex() {
    const katexContainer = this._katexContainer;
    if (!katexContainer) return;

    const latex = this.model.latex;

    katexContainer.replaceChildren();
    // @ts-expect-error lit hack won't fix
    delete katexContainer['_$litPart$'];

    if (!latex || latex.length === 0) {
      render(
        html`<span class="latex-block-empty-placeholder">Equation</span>`,
        katexContainer
      );
    } else {
      try {
        katex.render(latex, katexContainer, {
          displayMode: true,
        });
      } catch {
        katexContainer.replaceChildren();
        // @ts-expect-error lit hack won't fix
        delete katexContainer['_$litPart$'];
        render(
          html`<span class="latex-block-error-placeholder">Error equation</span>`,
          katexContainer
        );
      }
    }
  }

  private _handleClick() {
    if (this.doc.readonly) return;

    if (this.isBlockSelected) {
      this.toggleEditor();
    } else {
      this.selectBlock();
    }
  }

  removeEditor(portal: HTMLDivElement) {
    portal.remove();
  }

  override renderBlock() {
    return html`
      <div
        contenteditable="false"
        class="latex-block-container"
        @click=${this._handleClick}
      >
        <div class="katex"></div>
      </div>
    `;
  }

  selectBlock() {
    this.host.selection.setGroup('note', [
      this.host.selection.create('block', {
        blockId: this.model.id,
      }),
    ]);
  }

  toggleEditor() {
    const katexContainer = this._katexContainer;
    if (!katexContainer) return;

    this._editorAbortController?.abort();
    this._editorAbortController = new AbortController();

    this.selection.setGroup('note', []);

    const portal = createLitPortal({
      template: html`<latex-editor-menu
        .model=${this.model}
        .doc=${this.doc}
        .host=${this.host}
        .abortController=${this._editorAbortController}
      ></latex-editor-menu>`,
      container: this,
      closeOnClickAway: true,
      abortController: this._editorAbortController,
      shadowDom: false,
      portalStyles: {
        position: 'absolute',
        inset: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 'var(--affine-z-index-popover)',
        background: 'rgba(0, 0, 0, 0.03)',
        backdropFilter: 'blur(1px)',
        borderRadius: '4px',
      },
    });

    this._editorAbortController.signal.addEventListener(
      'abort',
      () => {
        this.removeEditor(portal);
      },
      { once: true }
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-latex': LatexBlockComponent;
  }
}
